'use client';

import { FC, useRef } from 'react';
import { StyledWAButton, Wrapper } from './styles';
import { useFormik } from 'formik';
import { Button, Divider, Typography } from '@mui/material';
import { WhatsAppIcon } from '@/components';
import { useSendRequestForm } from './hooks';
import { FormFields } from './FormFields';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { IHousingObjectResponse } from '@/types';

type SendRequestFormProps = {
  objectId: string;
  whatsappDisabled: boolean;
  hasInfoRequest?: boolean;
  onComplete?: () => void;
  listingData?: IHousingObjectResponse | null;
};

export const SendRequestForm: FC<SendRequestFormProps> = ({
  objectId,
  whatsappDisabled,
  hasInfoRequest,
  onComplete,
  listingData,
}) => {
  const t = useTranslations();

  const queryClient = useQueryClient();
  const resetFormRef = useRef<(() => void) | null>(null);

  const handleComplete = () => {
    const objectKey = String(objectId);

    void queryClient.invalidateQueries({ queryKey: ['housing-object', objectKey] });
    void queryClient.invalidateQueries({ queryKey: ['broker-messages', objectKey] });
    void queryClient.invalidateQueries({ queryKey: ['broker-last-message'] });
    void queryClient.invalidateQueries({ queryKey: ['housing-list'] });
    void queryClient.invalidateQueries({ queryKey: ['watchList'] });

    onComplete?.();
    resetFormRef.current?.();
  };

  const {
    initialValues,
    validationSchema,
    handleSubmit,
    isDisabled,
    isPending,
    isCheckWhatsappStatusPending,
    isLastRequestLoading,
    triggerCooldown,
    cooldown,
    whatsappUnavailable,
  } = useSendRequestForm({ objectId, listingData, onComplete: handleComplete });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => handleSubmit(values, false),
  });

  resetFormRef.current = formik.resetForm;

  const handleWASubmit = async () => {
    const errors = await formik.validateForm({ ...formik.values, sendWhatsapp: true });

    if (Object.keys(errors).length > 0) {
      void formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      );
      formik.setErrors(errors);
      return;
    }

    void handleSubmit(formik.values, true);
    triggerCooldown();
  };

  const isButtonDisabled = isDisabled || cooldown || isPending || isCheckWhatsappStatusPending;

  if (!objectId) return null;

  return (
    <Wrapper className="safari-no-autofill-ui">
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <FormFields formik={formik} isDisabled={isDisabled} />
        <Button
          variant="secondary"
          type="submit"
          sx={{
            height: '44px',
            width: '100% ',
          }}
          disabled={isButtonDisabled}
        >
          <Typography textTransform={'none'} fontWeight={600}>
            {isPending
              ? t('loading')
              : t(
                  `housingCardDetails.form.${hasInfoRequest ? 'sendAnotherRequest' : 'sendRequest'}`,
                )}
          </Typography>
        </Button>

        {!whatsappDisabled && !whatsappUnavailable && (
          <>
            <Divider sx={{ padding: '12px 0' }}>
              <Typography fontWeight={500} lineHeight={'16px'} fontSize={14}>
                {t('or')}
              </Typography>
            </Divider>

            <StyledWAButton disabled={isButtonDisabled} onClick={handleWASubmit}>
              {!isCheckWhatsappStatusPending && !isLastRequestLoading && (
                <WhatsAppIcon disabled={isButtonDisabled} />
              )}
              <Typography textTransform={'none'} fontWeight={600}>
                {isCheckWhatsappStatusPending
                  ? t('loading')
                  : t(
                      `housingCardDetails.form.${hasInfoRequest ? 'sendAnotherWhatsAppRequest' : 'sendWhatsAppRequest'}`,
                    )}
              </Typography>
            </StyledWAButton>
          </>
        )}
      </form>
    </Wrapper>
  );
};
